import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  LitSquadAvatarRecipe,
  LitSquadAvatarSaveResult,
  LitSquadInventoryEntry,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseLitSquadAvatarRecipeReturn {
  /** The saved recipe, resolved to items the player still owns; null when none. */
  recipe: LitSquadAvatarRecipe | null;
  /** Inventory entries for every item in `recipe`. */
  items: LitSquadInventoryEntry[];
  /** True once the saved recipe has loaded (or failed to). */
  loaded: boolean;
  saving: boolean;
  /** The last save's failure, or null. */
  error: string | null;
  /**
   * Changes when a save succeeds (a timestamp, unique across visits). Append
   * as `?v=${savedVersion}` to the username-derived avatar URL to bust the
   * browser cache (the CDN cache policy ignores query strings, and the stored
   * image was just replaced).
   */
  savedVersion: number;
  /** Save the avatar: the server renders the images from this recipe. */
  save: (recipe: LitSquadAvatarRecipe) => Promise<void>;
}

/**
 * The player's avatar recipe — which base character and accessories the
 * avatar is made of. Saving it is saving the avatar: the server renders both
 * images from it, and the editor reopens on it.
 */
export function useLitSquadAvatarRecipe(): UseLitSquadAvatarRecipeReturn {
  const queryClient = useQueryClient();
  // A timestamp, not a counter: a counter restarts every visit, so a URL like
  // `?v=1` would repeat and the browser would show the avatar it cached then.
  const [savedVersion, setSavedVersion] = useState(() => Date.now());

  const query = useQuery({
    queryKey: queryKeys.litSquad.avatar(),
    queryFn: () => api.getLitSquadAvatar(),
  });

  const mutation = useMutation<LitSquadAvatarSaveResult, Error, LitSquadAvatarRecipe>({
    mutationFn: (recipe) => api.saveLitSquadAvatar(recipe),
    onSuccess: () => {
      setSavedVersion(Date.now());
      queryClient.invalidateQueries({ queryKey: queryKeys.litSquad.avatar() });
    },
  });

  return {
    recipe: query.data?.recipe ?? null,
    items: query.data?.items ?? [],
    loaded: !query.isLoading,
    saving: mutation.isPending,
    error: mutation.error ? mutation.error.message : null,
    savedVersion,
    save: async (recipe) => {
      await mutation.mutateAsync(recipe);
    },
  };
}
